import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import ArenaCard from '../../common/models/ArenaCard';
import CardComponent from './CardComponent';
import { transPlain, ARENA_ICONS } from '../../common';
import { battlePassConfig, battlePassProgress, battlePassProgressText } from '../battlePass';
/**
 * The Battle Pass level track: one cell per level from 1 to the configured
 * cap, reward thumbnails above each level node and a progress line filled
 * up to the current level (with a partial segment toward the next one).
 *
 * The track is a slider: horizontally scrollable with chevron buttons on
 * both sides (shown only when the levels overflow), auto-centered on the
 * current level. Entrance animations run through the globally available
 * anime.js (window.anime — loaded by the forum theme); everything degrades
 * gracefully to static rendering when it's absent.
 *
 * Level details (full-size card art, card back, +HP/+mana) show in a
 * custom popup on hover/tap. The popup element is appended to
 * document.body and positioned with fixed viewport coordinates — a plain
 * Tooltip/tippy child would be clipped by the panel's overflow:hidden and
 * the track's own scroll container (that's exactly the bug this replaced).
 *
 * Shared between ArenaProfilePage (below the stats) and BattlePassModal.
 * Renders nothing when no levels are configured in the admin panel.
 */
export default class BattlePassBar extends Component {
  /** Chevrons are only shown when the track actually overflows. */
  canScroll = false;
  /** Entrance animation must run once per component life, not per redraw. */
  animated = false;

  /** Body-appended popup element (see class doc), created lazily. */
  popupEl = null;
  openLevel = null;
  /**
   * Pointer type of the most recent pointerdown on a cell. On touch, a tap
   * fires a synthetic mouseenter right before click — without telling the
   * two apart, the tap used to open the popup (enter) and instantly toggle
   * it closed again (click). Hover handlers act only for real mice; click
   * toggles only for touch/pen.
   */
  lastPointerType = 'mouse';
  oninit(vnode) {
    super.oninit(vnode);
    this.config = battlePassConfig();
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.scrollToCurrent();
    this.measureOverflow();
    this.playEntrance();

    // A popup positioned at fixed coordinates would drift away from its
    // level cell while the track scrolls under it — just dismiss it.
    this.track()?.addEventListener('scroll', this.hidePopup, {
      passive: true
    });

    // Tapping outside any level cell dismisses the popup (touch has no
    // mouseleave). Capture phase so it still runs if a handler inside
    // stops propagation.
    document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    this.measureOverflow();
  }
  onremove(vnode) {
    this.track()?.removeEventListener('scroll', this.hidePopup);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
    if (this.popupEl) {
      m.render(this.popupEl, null);
      this.popupEl.remove();
      this.popupEl = null;
    }
    super.onremove(vnode);
  }
  onDocumentPointerDown = e => {
    if (this.openLevel === null) return;
    const target = e.target;
    if (!target?.closest('.ArenaBattlePass-level')) {
      this.hidePopup();
    }
  };
  track() {
    return this.element?.querySelector('.ArenaBattlePass-track') ?? null;
  }

  /**
   * Center the current level in the scrollable track — with dozens of
   * levels the interesting part is where the player actually is, not
   * level 1 (same idea as game battle passes opening on your level).
   */
  scrollToCurrent() {
    const track = this.track();
    const current = track?.querySelector('.ArenaBattlePass-level--current');
    if (!track || !current) return;
    track.scrollLeft = Math.max(0, current.offsetLeft - track.clientWidth / 2 + current.clientWidth / 2);
  }
  measureOverflow() {
    const track = this.track();
    if (!track) return;
    const canScroll = track.scrollWidth > track.clientWidth + 4;
    if (canScroll !== this.canScroll) {
      this.canScroll = canScroll;
      m.redraw();
    }
  }
  scrollByPage(direction) {
    const track = this.track();
    if (!track) return;
    track.scrollBy({
      left: direction * Math.max(160, track.clientWidth * 0.7),
      behavior: 'smooth'
    });
  }

  /**
   * Entrance choreography via the theme's global anime.js: level nodes pop
   * in with a stagger, reward tiles float up, and the progress line sweeps
   * from empty to its actual fill. Runs exactly once per mount; no-op
   * without window.anime — the CSS already renders the final state. All
   * animated properties are one-shot, so there's no ongoing per-frame work
   * after the ~700ms entrance (the only infinite animation in the design
   * is the CSS pulse on the single current-level node).
   */
  playEntrance() {
    const anime = window.anime;
    if (!anime || this.animated || !this.element) return;
    this.animated = true;
    const nodes = this.element.querySelectorAll('.ArenaBattlePass-node');
    const rewards = this.element.querySelectorAll('.ArenaBattlePass-reward');
    const fills = this.element.querySelectorAll('.ArenaBattlePass-segmentFill');
    anime({
      targets: nodes,
      scale: [0.3, 1],
      opacity: [0, 1],
      delay: anime.stagger(28),
      duration: 520,
      easing: 'easeOutBack'
    });
    anime({
      targets: rewards,
      translateY: [10, 0],
      opacity: [0, 1],
      delay: anime.stagger(28, {
        start: 120
      }),
      duration: 480,
      easing: 'easeOutQuad'
    });
    fills.forEach((el, i) => {
      const target = el.dataset.fill || '0';
      el.style.width = '0%';
      anime({
        targets: el,
        width: `${target}%`,
        delay: 140 + i * 26,
        duration: 420,
        easing: 'easeInOutQuad'
      });
    });
  }

  // ── Level-details popup ───────────────────────────────────────────────

  showPopup(level, cell) {
    if (!this.popupEl) {
      this.popupEl = document.createElement('div');
      this.popupEl.className = 'ArenaBattlePassPopup';
      document.body.appendChild(this.popupEl);
    }
    this.openLevel = level;
    m.render(this.popupEl, this.popupContent(level));

    // Measure AFTER rendering the content, then clamp to the viewport.
    const rect = cell.getBoundingClientRect();
    const width = this.popupEl.offsetWidth;
    const height = this.popupEl.offsetHeight;
    let left = rect.left + rect.width / 2 - width / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    let top = rect.top - height - 10;
    if (top < 8) top = rect.bottom + 10;
    this.popupEl.style.left = `${Math.round(left)}px`;
    this.popupEl.style.top = `${Math.round(top)}px`;
    this.popupEl.classList.add('ArenaBattlePassPopup--visible');
  }

  // Arrow function: used directly as a scroll listener (see oncreate).
  hidePopup = () => {
    this.openLevel = null;
    this.popupEl?.classList.remove('ArenaBattlePassPopup--visible');
  };
  togglePopup(level, cell) {
    if (this.openLevel === level) this.hidePopup();else this.showPopup(level, cell);
  }
  popupContent(level) {
    const rewards = this.config.levels.get(level) ?? [];
    return m("div", {
      className: "ArenaBattlePassPopup-inner"
    }, m("div", {
      className: "ArenaBattlePassPopup-title"
    }, transPlain('forumaker-arena.forum.battle_pass.level_label', {
      level: String(level)
    })), rewards.length ? rewards.map(reward => this.popupReward(reward)) : m("div", {
      className: "ArenaBattlePassPopup-empty"
    }, transPlain('forumaker-arena.forum.battle_pass.no_reward')));
  }
  popupReward(reward) {
    switch (reward.type) {
      case 'card':
        {
          // Full playable card with stats when the serializer attached the
          // attributes bag (see extend.php) — a detached read-only model is
          // enough for CardComponent, no store required.
          if (reward.card) {
            const card = new ArenaCard({
              type: 'arena-cards',
              id: String(reward.id ?? 0),
              attributes: reward.card
            });
            return m("div", {
              className: "ArenaBattlePassPopup-reward ArenaBattlePassPopup-reward--card"
            }, m(CardComponent, {
              card: card
            }));
          }
          return m("div", {
            className: "ArenaBattlePassPopup-reward"
          }, reward.imageUrl && m("img", {
            className: "ArenaBattlePassPopup-cardImg",
            src: reward.imageUrl,
            alt: ""
          }), m("span", {
            className: "ArenaBattlePassPopup-name"
          }, transPlain('forumaker-arena.forum.battle_pass.reward_card', {
            name: reward.name || '?'
          })));
        }
      case 'cardBack':
        return m("div", {
          className: "ArenaBattlePassPopup-reward"
        }, reward.imageUrl && m("img", {
          className: "ArenaBattlePassPopup-backImg",
          src: reward.imageUrl,
          alt: ""
        }), m("span", {
          className: "ArenaBattlePassPopup-name"
        }, transPlain('forumaker-arena.forum.battle_pass.reward_card_back', {
          name: reward.name || '?'
        })));
      case 'hp':
        return m("div", {
          className: "ArenaBattlePassPopup-reward ArenaBattlePassPopup-reward--stat"
        }, m("i", {
          className: ARENA_ICONS.hp,
          style: {
            color: 'var(--arena-hp)'
          }
        }), m("span", {
          className: "ArenaBattlePassPopup-name"
        }, transPlain('forumaker-arena.forum.battle_pass.reward_hp', {
          n: String(reward.amount ?? 0)
        })));
      case 'mana':
        return m("div", {
          className: "ArenaBattlePassPopup-reward ArenaBattlePassPopup-reward--stat"
        }, m("i", {
          className: ARENA_ICONS.mana,
          style: {
            color: 'var(--arena-mana)'
          }
        }), m("span", {
          className: "ArenaBattlePassPopup-name"
        }, transPlain('forumaker-arena.forum.battle_pass.reward_mana', {
          n: String(reward.amount ?? 0)
        })));
      default:
        return null;
    }
  }

  // ── Rendering ─────────────────────────────────────────────────────────

  view() {
    const {
      configured,
      maxLevel
    } = this.config;
    if (!configured) return null;
    const progress = battlePassProgress(this.attrs.games, this.config);
    const fraction = progress.isMax ? 0 : Math.min(1, progress.intoLevel / progress.needed);
    const cells = [];
    for (let level = 1; level <= maxLevel; level++) {
      cells.push(this.levelCell(level, progress.current, fraction));
    }

    // Optional banner set in the admin panel (Боевой Пропуск → Фон).
    // Painted as the panel's own background-image so it sits behind every
    // child with no pseudo-element/z-index trickery needed. Shared by both
    // surfaces since BattlePassModal renders this same component.
    const backgroundUrl = app.forum.attribute('arenaBattlePassBackgroundUrl');
    const style = backgroundUrl ? {
      backgroundImage: `url("${backgroundUrl}")`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    } : undefined;
    return m("div", {
      className: "ArenaBattlePass",
      style: style
    }, !this.attrs.hideHeader && m("div", {
      className: "ArenaBattlePass-header"
    }, m("span", {
      className: "ArenaBattlePass-progressBadge"
    }, battlePassProgressText(progress))), m("div", {
      className: `ArenaBattlePass-slider ${this.canScroll ? 'ArenaBattlePass-slider--scrollable' : ''}`
    }, m(Button, {
      className: "Button Button--icon ArenaBattlePass-nav ArenaBattlePass-nav--prev",
      icon: "fas fa-chevron-left",
      "aria-label": app.translator.trans('forumaker-arena.forum.battle_pass.scroll_back'),
      onclick: () => this.scrollByPage(-1)
    }), m("div", {
      className: "ArenaBattlePass-track"
    }, cells), m(Button, {
      className: "Button Button--icon ArenaBattlePass-nav ArenaBattlePass-nav--next",
      icon: "fas fa-chevron-right",
      "aria-label": app.translator.trans('forumaker-arena.forum.battle_pass.scroll_forward'),
      onclick: () => this.scrollByPage(1)
    })));
  }
  levelCell(level, current, fraction) {
    const rewards = this.config.levels.get(level) ?? [];
    const reached = level <= current;

    // The connector segment LEFT of this node: fully filled while the node
    // is reached, partially filled for the node right after the current one.
    const segmentFill = level <= current ? 100 : level === current + 1 ? Math.round(fraction * 100) : 0;
    const classes = ['ArenaBattlePass-level'];
    if (reached) classes.push('ArenaBattlePass-level--reached');
    if (level === current) classes.push('ArenaBattlePass-level--current');
    if (rewards.length) classes.push('ArenaBattlePass-level--hasReward');
    return m("div", {
      className: classes.join(' '),
      onpointerdown: e => {
        this.lastPointerType = e.pointerType || 'mouse';
      },
      onpointerenter: e => {
        if (e.pointerType === 'mouse') this.showPopup(level, e.currentTarget);
      },
      onpointerleave: e => {
        if (e.pointerType === 'mouse') this.hidePopup();
      },
      onclick: e => {
        // Touch/pen only — with a mouse the hover handlers own the popup
        // (see lastPointerType's doc for the tap-flicker this prevents).
        if (this.lastPointerType !== 'mouse') this.togglePopup(level, e.currentTarget);
      }
    }, m("div", {
      className: "ArenaBattlePass-reward"
    }, rewards.length ?
    // All of a level's rewards live in ONE pill badge — several
    // loose chips next to each other read as unrelated items.
    m("span", {
      className: "ArenaBattlePass-rewardGroup"
    }, rewards.slice(0, 3).map(reward => this.rewardIcon(reward))) : m("span", {
      className: "ArenaBattlePass-noReward"
    })), level > 1 && m("div", {
      className: "ArenaBattlePass-segment"
    }, m("span", {
      className: "ArenaBattlePass-segmentFill",
      "data-fill": String(segmentFill),
      style: {
        width: `${segmentFill}%`
      }
    })), m("div", {
      className: "ArenaBattlePass-node"
    }, m("span", null, level)));
  }
  rewardIcon(reward) {
    switch (reward.type) {
      case 'card':
        return reward.imageUrl ? m("img", {
          className: "ArenaBattlePass-thumb ArenaBattlePass-thumb--card",
          src: reward.imageUrl,
          alt: reward.name || '',
          loading: "lazy"
        }) :
        // fa-clone: a stacked-cards glyph, the same one ElementsGuideModal's
        // title uses (fa-id-card read as a black blob here). Not fa-cards —
        // that one is FontAwesome Pro and renders as nothing.
        m("span", {
          className: "ArenaBattlePass-chip"
        }, m("i", {
          className: "fas fa-clone"
        }));
      case 'cardBack':
        return reward.imageUrl ? m("img", {
          className: "ArenaBattlePass-thumb ArenaBattlePass-thumb--back",
          src: reward.imageUrl,
          alt: reward.name || '',
          loading: "lazy"
        }) : m("span", {
          className: "ArenaBattlePass-chip"
        }, m("i", {
          className: "fas fa-clone"
        }));
      case 'hp':
        return m("span", {
          className: "ArenaBattlePass-chip ArenaBattlePass-chip--hp"
        }, m("i", {
          className: ARENA_ICONS.hp
        }), "+", reward.amount ?? 0);
      case 'mana':
        return m("span", {
          className: "ArenaBattlePass-chip ArenaBattlePass-chip--mana"
        }, m("i", {
          className: ARENA_ICONS.mana
        }), "+", reward.amount ?? 0);
      default:
        return null;
    }
  }
}
flarum.reg.add('forumaker-arena', 'forum/components/BattlePassBar', BattlePassBar);